import { createSelector } from 'reselect';

import type { MayBe } from 'modules/global/typing/common/common';
import type { Label } from 'modules/global/typing/common/Label';
import { getByGuidelineIds } from 'modules/guidelines/selectors/index';
import { getFilters, getState as getParent } from 'modules/guidelines/selectors/ui/dashboard';
import { getLocale } from 'modules/i18n/selectors/data';
import { getDealerDefaultLanguage, getSelectedDealer, getSelectedDealerId, getSelectedDealerGroupId } from 'selectors/dealerSelectors';
import { uniq } from 'utils/ObjectUtils';
import { getProps, getRoot } from 'utils/selectors';

const getState = createSelector(getParent, state => state.config);

const getGuidelineIds = createSelector(getState, state => state.ids);

export const isLoading = createSelector(getState, state => state.loading);

export const getApiError = createSelector(getState, state => state.error);

export const getPage = createSelector(getState, state => state.page);

export const hasNextPage = createSelector(getState, ({ hasMore }) => hasMore);

const getSort = createSelector(getState, state => state.sort);

export const getParams = createSelector(
  getFilters,
  getSelectedDealerId,
  getSelectedDealerGroupId,
  getPage,
  getSort,
  getDealerDefaultLanguage,
  (filters, dealerId, groupId, { size }, sort, language) => ({
    ...filters,
    dealerId,
    groupId,
    sort,
    size,
    language,
  })
);

export const getName = (name: MayBe<Label | string>, UILocale: string, locale: string): MayBe<string> => {
  if (typeof name === 'object' && !Array.isArray(name)) {
    return (
      name[UILocale] ??
      name[locale] ??
      // API doesn't always provide the properties in the same order, we sort for consistency
      Object.keys(name)
        .sort()
        .map(key => name[key])
        .find(Boolean)
    );
  }
  return undefined;
};

const getGroupGuidelines = createSelector(
  getSelectedDealer,
  (dealer: { group?: { config?: { selectedGuidelines?: Record<string, string[]> } } }) => dealer.group?.config?.selectedGuidelines
);

const getDealerGuidelines = createSelector(
  getSelectedDealer,
  (dealer: { config?: { sales?: { selectedGuidelines?: Record<string, string[]> } } }) => dealer.config?.sales?.selectedGuidelines
);

export const getBrandGroupGuidelines = createSelector(getProps<string>, getGroupGuidelines, (brandId, group) => group?.[brandId]);

const getBrandDealerGuidelines = createSelector(getProps<string>, getDealerGuidelines, (brandId, dealer) => dealer?.[brandId]);

const getBrandGuidelineIds = createSelector(
  getBrandGroupGuidelines,
  getBrandDealerGuidelines,
  (groupGuidelines = [], dealerGuidelines = []) => [...groupGuidelines, ...dealerGuidelines]
);

const getDisplayedGuidelineIds = createSelector(
  getGuidelineIds,
  getBrandGuidelineIds,
  (ids, selected) => uniq([...ids, ...selected]) as string[]
);

const getDisplayedGuidelineItem = createSelector(getRoot, getDisplayedGuidelineIds, getByGuidelineIds);

export const getGuidelines = createSelector(
  getDisplayedGuidelineItem,
  getLocale,
  getDealerDefaultLanguage,
  (guidelines, UILocale, locale) => guidelines.map(guideline => ({
    ...guideline,
    name: getName(guideline.name, UILocale, locale),
  }))
);
